<template>
  <TwoFactorAction
    v-slot="twofactor"
    :show="canShow"
    :alert-context="alertContext"
    :two-factor-payload="twoFactorPayload"
    :submit-payload="submitActionPayload"
    @error="handleActionFailure"
    @success="handleActionSuccess"
    @close="handleActionClose"
    @gotoupdatecontact="goToUpdateContact"
    @closeverifyphonedialog="handleActionVerifyPhoneClose"
    ref="twofactoryactionacc"
    class="moveDialogAboveBottomNav"
  >
    <v-card id="verify_phone_card" v-if="verifyPhonePageToLand === 'first'" class="text-center">
      <v-container class="pa-0">
        <AlertsComponent :context="alertContext" />
      </v-container>
      <v-container>
        <v-card-text class="maxHeightMobileVerif">
          <v-row>
            <v-col cols="11">
              <h2 class="text-h2">
                {{ $t('L_get_quick_access_to_your_account_with_mobile_verification') }}
              </h2>
            </v-col>
          </v-row>
        </v-card-text>
        <v-row>
          <v-col cols="12">
            <v-row justify="center">
              <v-col cols="11" class="mb-4 text-left">
                <span class="font-weight-regular">{{ $t(modalContent.message) }}</span>
              </v-col>
              <v-col mb-4>
                <span class="font-weight-regular font-size--12">{{
                  $t('L_message_and_data_rates_may_apply')
                }}</span>
              </v-col>
            </v-row>
            <v-form id="phone-form" ref="phone-form" v-model="validForm" autocomplete="off">
              <div class="mb-2 mt-2 edit-wrapper d-flex justify-center align-center">
                <v-row class="pa-6 justify-center align-center">
                  <v-col cols="10" class="ma-0 pa-0 text-left">
                    <OnexPhone
                      id="verify_phone"
                      :class="{
                        'v-field--error v-field--active v-field--error input':
                          currentPhoneNumber === phoneNumber && phoneChanged,
                      }"
                      v-model="phoneNumber"
                      :place-holder="phoneNumber"
                      :label="$t('L_mobile_phone_number')"
                      :disabled="editIsDisabled"
                      @input="checkPhoneNumberModel"
                      required
                    />
                    <div
                      v-if="currentPhoneNumber === phoneNumber && phoneChanged"
                      class="text-error font-size--14 error-message-phone"
                    >
                      {{ $t('L_mobile_number_must_be_different') }}
                    </div>
                  </v-col>
                  <v-col cols="2" class="ma-0 pa-0">
                    <OnexButton
                      class="pa-0 ma-0"
                      v-if="!editDisabled"
                      id="pencil-edit-phone"
                      color="#063562"
                      aria-label="Edit Phone"
                      @click="editIsDisabled = false"
                      :prepend-icon="mdiPencil"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-form>
            <v-row justify="center">
              <v-col cols="10" class="text-center d-flex flex-column">
                <OnexButton
                  id="modal-btn"
                  block
                  :loading="loading"
                  color="primary"
                  class="mb-3"
                  :disabled="
                    !validForm ||
                    !phoneNumber ||
                    (currentPhoneNumber === phoneNumber && phoneChanged)
                  "
                  @click="twofactor.start"
                >
                  {{ $t(modalContent.buttonText) }}
                </OnexButton>
                <OnexButton
                  text="Outlined Medium"
                  @click="closeVerify"
                  variant="outlined"
                  color="outlined"
                >
                  {{ $t('L_skip_for_now') }}
                </OnexButton>
              </v-col>
            </v-row>
          </v-col>
          <v-col />
        </v-row>
      </v-container>
    </v-card>
  </TwoFactorAction>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import AlertsComponent from '../AlertsComponent.vue';
import TwoFactorAction from '../TwoFactorAction/TwoFactorAction.vue';
import { ALERT_VIEW_CONTEXT, CHANNELS } from '@/shared/models/constants.models';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapActions, mapState } from 'pinia';
import { useHomeStore } from '@/modules/home/store/home.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { OnexButton, OnexPhone } from '@primerica/onex-ui-lib';
import { mdiPencil, mdiClose } from '@mdi/js';

const componentOptions: ComponentOptions = {
  name: 'VerifyPhoneWrapper',
  components: {
    TwoFactorAction,
    AlertsComponent,
    OnexButton,
    OnexPhone,
  },
  props: {
    allowEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    show: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    editIsDisabled: true,
    phoneNumber: '',
    alertContext: ALERT_VIEW_CONTEXT.VERIFY_PHONE,
    showSuccessMessage: false,
    mdiPencil,
    mdiClose,
    validForm: false,
    checkPhoneNumbers: false,
    currentPhoneNumber: '',
    phoneChanged: false,
  }),
  computed: {
    ...mapState(useGlobalStore, {
      notifications: 'notifications',
      verifyPhonePageToLand: 'verifyPhonePageToLand',
      containsFeature: 'containsFeature',
      locale: 'getLocale',
    }),
    ...mapState(useHomeStore, {
      loading: 'loading',
    }),
    ...mapState(useCustomerStore, {
      customerProfile: 'customerProfile',
    }),
    twoFactorPayload() {
      return {
        channel: CHANNELS.SMS,
        phone: this.phoneNumber,
        locale: this.locale,
      };
    },
    mobileNumber() {
      return this.customerProfile.mobilePhone
        ? this.customerProfile.mobilePhone.trim()
        : this.phoneNumber;
    },
    canShow() {
      return this.show;
    },
    isVerifyModal() {
      return !!this.mobileNumber && this.mobileNumber.trim().length > 0;
    },
    modalContent() {
      return {
        message: this.isVerifyModal
          ? 'L_we_noticed_you_havenot_verified_your_mobile_number_yet'
          : 'L_no_mobile_number',
        buttonText: this.isVerifyModal
          ? 'L_yes_use_sms_verification'
          : 'L_verify_with_text_message',
      };
    },
    editDisabled() {
      return this.isVerifyModal && this.editIsDisabled && !this.allowEdit;
    },
    submitActionPayload() {
      return { mobileNumber: this.phoneNumber };
    },
  },
  methods: {
    ...mapActions(useGlobalStore, {
      addError: 'addError',
      addAlert: 'addAlert',
      snoozeVerifyPhone: 'snoozeVerifyPhone',
      removeNotification: 'removeNotification',
      updateNotification: 'updateNotification',
    }),
    ...mapActions(useCustomerStore, {
      updateCustomerProfile: 'updateCustomerProfile',
    }),
    async handleActionSuccess() {
      await this.updateCustomerProfile({
        mobilePhone: this.phoneNumber,
        phoneVerified: true,
      });
      // Since the phone is verified we will remove it from sessionStorage
      window.sessionStorage.removeItem('hideVerifyPhone');
      this.addAlert({
        type: 'success',
        details: { message: this.$t('L_phone_has_been_verified') },
        context: ALERT_VIEW_CONTEXT.VERIFY_PHONE,
      });
      this.$emit('show-successful-verify');
    },
    handleActionFailure(error: unknown) {
      this.addError({ error, context: this.alertContext });
    },
    handleActionClose() {
      this.$emit('close-verify-phone-dialog');
    },
    handleActionVerifyPhoneClose() {
      this.$emit('close-verify-phone-dialog');
    },
    goToUpdateContact() {
      this.$emit('gotoupdatecontact2');
    },
    async closeVerify() {
      await this.snoozeVerifyPhone();
      this.$emit('close-verify-phone-dialog');
    },
    async bypassPageOne(num: string) {
      this.phoneNumber = num;
      await this.$refs.twofactoryactionacc.requestOTP(num);
    },
    reset() {
      this.phoneNumber = this.mobileNumber.trim();
    },
    checkPhoneNumberModel() {
      this.phoneChanged = true;
    },
  },
  watch: {
    mobileNumber() {
      this.phoneNumber = this.mobileNumber.trim();
    },
  },
  mounted() {
    this.reset();
    this.currentPhoneNumber = this.phoneNumber;
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
.maxHeightMobileVerif {
  max-height: 125px;
}
.edit-wrapper {
  position: relative;
}
.fixed-top-alert {
  position: absolute;
  top: -4px;
  z-index: 1;
}
#verify_phone {
  width: 100%;
}
#pencil-edit-phone {
  height: 54px;
  position: relative;
  top: -5px;
  .v-btn__prepend {
    margin: 0 !important;
  }
}
.error-message-phone {
  position: absolute;
  bottom: 10px;
  left: 24px;
}
.moveDialogAboveBottomNav {
  z-index: 10000 !important;
}
</style>
